export default {
  'article.guide.title': 'USFans Spreadsheet Guide: Organize & Shop the Full Catalog',
  'article.guide.desc':
    'A complete guide to the USFans Spreadsheet — how it organizes fashion categories, how to browse efficiently, and smart shopping tips.',
  'article.guide.category': 'Complete Guide',

  'article.meta.title': 'USFans Spreadsheet Guide: Organize & Shop the Full Catalog',
  'article.meta.description':
    'Learn how the USFans Spreadsheet organizes shoes, hoodies, jerseys and more into clear categories. A practical guide with browsing tips, comparisons and FAQs.',
  'article.breadcrumb.home': 'Home',
  'article.breadcrumb.current': 'USFans Spreadsheet Guide',

  'article.author': 'USFans Spreadsheet Editorial Team',
  'article.updated': 'Last updated',
  'article.minutes': 'min read',

  'article.intro.title': 'Introduction',
  'article.intro.p1':
    'Shopping across a large fashion catalog can feel overwhelming, especially when products are spread across dozens of pages. The USFans Spreadsheet was created to solve exactly that problem: it brings shoes, hoodies, t-shirts, jackets, jerseys, accessories and more into one organized, easy-to-navigate overview.',
  'article.intro.p2':
    'In this guide, we explain what the USFans Spreadsheet is, how its category system works, and how you can use it to find the right item faster — without endless scrolling or guesswork.',

  'article.what.title': 'What Is the USFans Spreadsheet?',
  'article.what.p1':
    'The USFans Spreadsheet is a structured catalog that groups a wide range of fashion and fan merchandise into clear, logical categories. Rather than browsing a single unorganized product stream, visitors can jump directly to the category they care about — shoes, hoodies and sweaters, t-shirts, jackets, pants and shorts, headwear, sets, underwear, jerseys, accessories, bags, belts, perfumes and other items.',
  'article.what.p2':
    'Each category connects to a dedicated section of the main store, so you always land exactly where you need to be. This structure makes the USFans Spreadsheet useful as both a quick navigation tool and a planning reference for your next purchase.',

  'article.categories.title': 'Understanding the Category System',
  'article.categories.p1':
    'The catalog is organized into fourteen intuitive categories. Here is how each one helps you narrow down your search:',
  'article.categories.bullet1':
    'Shoes — sneakers, casual footwear and athletic styles for everyday wear.',
  'article.categories.bullet2':
    'Hoodies/Sweaters — cozy pullovers, hoodies and knitwear for layering.',
  'article.categories.bullet3':
    'T-Shirts — everyday tees in a variety of fits and designs.',
  'article.categories.bullet4':
    'Jackets — outerwear from light layers to heavier coats.',
  'article.categories.bullet5':
    'Pants/Shorts — bottoms for every season and occasion.',
  'article.categories.bullet6':
    'Headwear — caps, beanies and other head accessories.',
  'article.categories.bullet7':
    'Sets — coordinated outfits bundled together.',
  'article.categories.bullet8':
    'Underwear/Underpants — essential basics and undergarments.',
  'article.categories.bullet9':
    'Jersey — team and sports-inspired jerseys.',
  'article.categories.bullet10':
    'Accessories — small items that complete an outfit.',
  'article.categories.bullet11':
    'Bags — backpacks, totes and everyday carry options.',
  'article.categories.bullet12':
    'Belts — waist belts in a range of styles.',
  'article.categories.bullet13':
    'Perfumes — fragrances for personal use or gifting.',
  'article.categories.bullet14':
    'Other Stuff — everything else that does not fit the categories above.',

  'article.how.title': 'How to Browse the Catalog Efficiently',
  'article.how.p1':
    'Start by identifying what you actually need. If you are looking for a specific item, use the category dropdown or category buttons on the homepage to jump straight to that section. If you are just exploring, browse the featured categories first to see what is most popular.',
  'article.how.p2':
    'Once you are inside a category, look at the full selection before deciding. Comparing a few options within the same category — rather than jumping between unrelated products — usually leads to a better choice and fewer regrets.',
  'article.how.tip1': 'Use categories instead of guessing search terms.',
  'article.how.tip2': 'Compare a few items within the same category.',
  'article.how.tip3': 'Check availability and details before finalizing.',

  'article.comparison.title': 'USFans Spreadsheet vs. Unorganized Browsing',
  'article.comparison.p1':
    'The difference between an organized catalog and a disorganized product list is significant. The table below summarizes how the USFans Spreadsheet approach compares to other ways of browsing.',
  'article.comparison.col1': 'Approach',
  'article.comparison.col2': 'Organization',
  'article.comparison.col3': 'Speed to find item',
  'article.comparison.col4': 'Best use case',
  'article.comparison.r1c1': 'USFans Spreadsheet',
  'article.comparison.r1c2': 'High',
  'article.comparison.r1c3': 'Fast',
  'article.comparison.r1c4': 'Structured shopping',
  'article.comparison.r2c1': 'Random search',
  'article.comparison.r2c2': 'Low',
  'article.comparison.r2c3': 'Slow',
  'article.comparison.r2c4': 'Quick one-off lookups',
  'article.comparison.r3c1': 'Single long list',
  'article.comparison.r3c2': 'Medium',
  'article.comparison.r3c3': 'Medium',
  'article.comparison.r3c4': 'Scrolling inspiration',

  'article.tips.title': 'Smart Shopping Tips',
  'article.tips.p1':
    'A little preparation makes online shopping smoother and safer. Keep these practical tips in mind when using the USFans Spreadsheet:',
  'article.tips.tip1': 'Double-check product details, sizes and availability before ordering.',
  'article.tips.tip2': 'Review shipping and return information on the store itself.',
  'article.tips.tip3': 'Keep your payment details private and only shop on the official store.',
  'article.tips.tip4': 'Revisit the catalog when new items or seasons arrive.',

  'article.faq.title': 'Frequently Asked Questions',
  'article.faq.q1': 'What is the USFans Spreadsheet?',
  'article.faq.a1':
    'It is an organized catalog that groups fashion and fan merchandise into clear categories, making it faster to find shoes, hoodies, jerseys, accessories and more.',
  'article.faq.q2': 'How do I find a specific product quickly?',
  'article.faq.a2':
    'Use the category dropdown or the category buttons on the homepage to jump straight to the section you need.',
  'article.faq.q3': 'Is the USFans Spreadsheet a store?',
  'article.faq.a3':
    'It is an informational and navigation resource. Purchases are completed on the main store it links to.',
  'article.faq.q4': 'How often is the catalog updated?',
  'article.faq.a4':
    'Categories are kept aligned with the main store, so new arrivals and seasonal items appear as they become available.',

  'article.cta.title': 'Ready to Browse the Full Catalog?',
  'article.cta.p1':
    'Now that you understand how the USFans Spreadsheet works, head back to the homepage to explore categories, or visit the store directly to start shopping.',
  'article.cta.back': 'Back to Homepage',
  'article.cta.store': 'Visit the Store',

  'article.disclaimer':
    'Disclaimer: USFanslink.makeup is an independent informational resource. Product availability, pricing and policies are managed by the linked store and may change. Always verify important details directly on the store before purchasing.',
};